import React, { FC } from 'react';
import { Datagrid, Filter, TextInput, usePermissions, TextField } from 'react-admin';
import { List } from '~/layout';
import { DateTimeInput, NoPermissions, StatusField } from '~/components/ra';
import { useExternalProfileLinkTranslate } from '.';
import ReferenceIdField from './components/ReferenceIdField';
import ListDateTimeField from './components/ListDateTimeField';
import { SelectInput } from 'react-admin';
import { useExternalProfileLinkSystems } from '~/hooks/externalProfileLinks';
import BackgroundTaskExport from '~/components/BackgroundTaskExport';
import { useListContext } from 'react-admin';
import { omit } from 'lodash';
import ReferenceField from '~/components/ReferenceField';
import { ExternalProfileLink } from '~/types/ExternalProfileLink';
import { FunctionField } from 'react-admin';
import { useDefaultListFilterStyles } from '~/hooks/useListFilterStyles';

const ExternalProfileLinkList: FC<any> = (props) => {
  const t = useExternalProfileLinkTranslate();

  const { permissions } = usePermissions();

  const classes = useDefaultListFilterStyles();

  const { data: externalProfileLinkSystems, isLoading } = useExternalProfileLinkSystems();

  if (!permissions) return null;
  if (!permissions?.includes('externalProfileLink.list')) return <NoPermissions />;

  return (
    <List
      bulkActionButtons={false}
      {...props}
      filters={
        <Filter style={{ maxWidth: 1060 }} className={classes.form}>
          <SelectInput
            label={t('filter.profileType')}
            source='profileType'
            choices={[
              { id: 'INDIVIDUAL', name: t('profileTypeInput.INDIVIDUAL') },
              { id: 'BUSINESS', name: t('profileTypeInput.BUSINESS') },
            ]}
            alwaysOn
            allowEmpty
            emptyText={t('profileTypeInput.any')}
            className={classes.formControl}
          />
          <TextInput
            label={t('filter.profileId')}
            source='profileId'
            alwaysOn
            className={classes.formControl}
          />
          <SelectInput
            label={t('filter.system')}
            source='externalSourceSystem'
            choices={(externalProfileLinkSystems || []).map((system) => ({
              id: system,
              name: system,
            }))}
            alwaysOn
            allowEmpty
            emptyText={t('systemInput.any')}
            className={classes.formControl}
            disabled={isLoading}
          />
          <TextInput
            label={t('filter.referenceId')}
            source='referenceId'
            alwaysOn
            className={classes.formControl}
          />
          <SelectInput
            label={t('filter.status')}
            source='status'
            choices={[
              { id: 'ACTIVE', name: t('statusInput.ACTIVE') },
              { id: 'CLOSED', name: t('statusInput.CLOSED') },
              { id: 'NOT_READY', name: t('statusInput.NOT_READY') },
              { id: 'REJECTED', name: t('statusInput.REJECTED') },
              { id: 'REQUESTED', name: t('statusInput.REQUESTED') },
            ]}
            alwaysOn
            allowEmpty
            emptyText={t('statusInput.any')}
            className={classes.formControl}
          />
          <DateTimeInput
            source='createdFrom'
            label={t('filter.createdFrom')}
            options={{ format: 'LL LT', clearable: true }}
            fullWidth
            alwaysOn
            className={classes.formControl}
          />
          <DateTimeInput
            source='createdTo'
            label={t('filter.createdTo')}
            options={{ format: 'LL LT', clearable: true }}
            fullWidth
            alwaysOn
            className={classes.formControl}
          />
          <DateTimeInput
            source='updatedFrom'
            label={t('filter.updatedFrom')}
            options={{ format: 'LL LT', clearable: true }}
            fullWidth
            alwaysOn
            className={classes.formControl}
          />
          <DateTimeInput
            source='updatedTo'
            label={t('filter.updatedTo')}
            options={{ format: 'LL LT', clearable: true }}
            fullWidth
            alwaysOn
            className={classes.formControl}
          />
        </Filter>
      }
      // actions={<ApiIdentitiesActions {...props} />}
      actions={
        <div style={{ minWidth: 240, marginTop: 'auto', textAlign: 'right' }}>
          <ExportAction {...props} />
        </div>
      }
    >
      <Datagrid rowClick='show' size='medium' selectedIds={null}>
        <FunctionField
          label={t('fields.profile')}
          render={(record: ExternalProfileLink) => (
            <ReferenceField
              record={{
                id: record?.profileId,
                type: record?.profileType,
              }}
            />
          )}
        />
        <TextField label={t('fields.system')} source='externalSourceSystem' sortable={false} />
        <ReferenceIdField label={t('fields.referenceId')} />
        <StatusField source='status' sortable={false} />
        <ListDateTimeField label={t('fields.created')} source='created' sortable={false} />
        <ListDateTimeField label={t('fields.updated')} source='updated' sortable={false} />
      </Datagrid>
    </List>
  );
};

const handleFilters = (filters: any) => {
  if (!filters) return {};
  return filters;
};

const ExportAction = () => {
  const { filterValues } = useListContext();
  // For backend compatibility we need to change the key from roles to role.
  const restFilterValues = omit(filterValues, ['roles']);
  const newFilterValues = { ...restFilterValues, role: filterValues.roles || [] };
  return (
    <BackgroundTaskExport
      type='EXTERNAL_PROFILE_LINK_EXPORT'
      queryArguments={handleFilters(newFilterValues)}
    />
  );
};

export default ExternalProfileLinkList;
